import { useEffect, useRef, useState } from "react";
import api, { formatApiError, resolveImageUrl } from "@/lib/api";
import { useCategories } from "@/contexts/CategoriesContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Upload, Copy, GripVertical, ChevronDown, ChevronUp, FileText } from "lucide-react";

import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const EMPTY = {
  name: "", slug: "", category: "",
  short_desc: "", long_desc: "",
  presentation: "Pre-Mixed Pen", dose: "",
  price: 0, image_url: "", image_url_2: "",
  coa_url: "",
  in_stock: true,
};

function ImageUploadField({ value, onChange, testIdPrefix, label }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-2 flex gap-2">
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… or upload"
          className="flex-1"
          data-testid={`${testIdPrefix}-url`}
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={onUpload}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testIdPrefix}-upload`}
        >
          <Upload className="w-4 h-4 mr-2" />
          {uploading ? "…" : "Upload"}
        </Button>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onChange("")}
            data-testid={`${testIdPrefix}-clear`}
            title="Remove"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
      {value && (
        <img src={resolveImageUrl(value)} alt="" className="mt-2 w-24 h-24 object-cover border border-border bg-secondary" />
      )}
    </div>
  );
}

function CoaUploadField({ value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("COA must be a PDF file");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-coa", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("COA uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <Label>Certificate of Analysis (PDF)</Label>
      <div className="mt-2 flex gap-2">
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="/api/media/… or upload"
          className="flex-1"
          data-testid="product-coa-url"
        />
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf"
          onChange={onUpload}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="product-coa-upload"
        >
          <Upload className="w-4 h-4 mr-2" />
          {uploading ? "…" : "Upload"}
        </Button>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onChange("")}
            data-testid="product-coa-clear"
            title="Remove"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
      {value && (
        <a
          href={resolveImageUrl(value)}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center gap-2 text-xs underline text-foreground"
          data-testid="product-coa-preview"
        >
          <FileText className="w-3.5 h-3.5" /> View uploaded PDF
        </a>
      )}
    </div>
  );
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const { categories } = useCategories();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (p) => {
    setEditing(p);
    setForm({ ...EMPTY, ...p, image_url_2: p.image_url_2 || "", coa_url: p.coa_url || "" });
    setOpen(true);
  };

  const save = async () => {
    setError(null);
    try {
      const payload = { ...form, price: Number(form.price) };
      if (editing) {
        await api.put(`/products/${editing.product_id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setOpen(false);
      load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    await api.delete(`/products/${p.product_id}`);
    load();
  };

  const duplicate = (p) => {
    setEditing(null);
    // Strip server-managed fields and append "(copy)" + slug suffix
    const { product_id, created_at, slug, name, ...rest } = p;
    const suffix = `-copy-${Math.random().toString(36).slice(2, 6)}`;
    setForm({
      ...EMPTY,
      ...rest,
      image_url_2: p.image_url_2 || "",
      coa_url: p.coa_url || "",
      name: `${name} (copy)`,
      slug: `${slug}${suffix}`,
    });
    setOpen(true);
  };

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  return (
    <div data-testid="admin-products" className="space-y-6">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="overline">Catalog</div>
          <h2 className="font-display text-2xl font-semibold mt-1">Products</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Grouped by category. Drag <GripVertical className="w-3 h-3 inline -mt-1" /> to reorder products within a category.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={startNew} className="rounded-md" data-testid="product-new">
              <Plus className="w-4 h-4 mr-2" /> New product
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit product" : "New product"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label>Name</Label><Input value={form.name} onChange={update("name")} className="mt-2" /></div>
              <div><Label>Slug</Label><Input value={form.slug} onChange={update("slug")} className="mt-2" /></div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger className="mt-2"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.category_id} value={c.slug}>{c.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Presentation</Label><Input value={form.presentation} onChange={update("presentation")} className="mt-2" /></div>
              <div><Label>Dose</Label><Input value={form.dose} onChange={update("dose")} className="mt-2" /></div>
              <div><Label>Price (USD)</Label><Input type="number" step="0.01" value={form.price} onChange={update("price")} className="mt-2" /></div>
              <div className="sm:col-span-2 grid sm:grid-cols-2 gap-3">
                <ImageUploadField
                  label="Image 1 (main)"
                  value={form.image_url}
                  onChange={(v) => setForm((f) => ({ ...f, image_url: v }))}
                  testIdPrefix="product-image-1"
                />
                <ImageUploadField
                  label="Image 2 (optional)"
                  value={form.image_url_2}
                  onChange={(v) => setForm((f) => ({ ...f, image_url_2: v }))}
                  testIdPrefix="product-image-2"
                />
              </div>
              <div className="sm:col-span-2">
                <CoaUploadField
                  value={form.coa_url}
                  onChange={(v) => setForm((f) => ({ ...f, coa_url: v }))}
                />
              </div>
              <div className="sm:col-span-2"><Label>Short description</Label><Input value={form.short_desc} onChange={update("short_desc")} className="mt-2" /></div>
              <div className="sm:col-span-2"><Label>Long description</Label><Textarea value={form.long_desc} onChange={update("long_desc")} rows={3} className="mt-2" /></div>
            </div>
            {error && <div className="text-sm text-destructive">{error}</div>}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="product-save">{editing ? "Update" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <ProductsByCategory
        products={products}
        categories={categories}
        onEdit={startEdit}
        onDuplicate={duplicate}
        onDelete={remove}
        onReorder={async (categorySlug, newOrderIds) => {
          // Optimistic local re-order
          setProducts((prev) => {
            const map = new Map(prev.map((p) => [p.product_id, p]));
            const reordered = newOrderIds.map((id, idx) => ({ ...map.get(id), order: idx }));
            const others = prev.filter((p) => p.category !== categorySlug);
            return [...others, ...reordered];
          });
          try {
            await api.post("/admin/products/reorder", { category: categorySlug, ids: newOrderIds });
          } catch (e) {
            toast.error(formatApiError(e));
            load();
          }
        }}
      />
    </div>
  );
}

function SortableProductRow({ p, onEdit, onDuplicate, onDelete }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: p.product_id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : "auto",
    background: isDragging ? "#f8fafc" : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`product-row-${p.slug}`}
      className="p-3 sm:p-4 grid grid-cols-[auto_auto_1fr_auto] md:grid-cols-12 items-center gap-2 md:gap-3"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
        data-testid={`product-drag-${p.slug}`}
        className="md:col-span-1 cursor-grab active:cursor-grabbing touch-none p-2 -m-2 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <img src={resolveImageUrl(p.image_url)} alt="" className="md:col-span-1 w-12 h-12 object-cover bg-secondary border border-border" />
      <div className="md:col-span-4 min-w-0">
        <div className="font-display font-semibold truncate">{p.name}</div>
        <div className="text-xs text-muted-foreground truncate">{p.slug}</div>
      </div>
      <div className="hidden md:block md:col-span-2 text-xs text-muted-foreground truncate">{p.presentation}</div>
      <div className="hidden md:block md:col-span-1 font-mono text-sm">${p.price.toFixed(2)}</div>
      <div className="col-start-4 md:col-span-3 flex md:justify-end gap-1.5">
        <Button size="sm" variant="outline" onClick={() => onEdit(p)} data-testid={`edit-${p.slug}`} title="Edit" className="h-8 w-8 p-0">
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button size="sm" variant="outline" onClick={() => onDuplicate(p)} data-testid={`duplicate-${p.slug}`} title="Duplicate" className="h-8 w-8 p-0">
          <Copy className="w-3.5 h-3.5" />
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onDelete(p)} data-testid={`del-${p.slug}`} title="Delete" className="h-8 w-8 p-0">
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

function ProductsByCategory({ products, categories, onEdit, onDuplicate, onDelete, onReorder }) {
  // Group products by category slug, sorted by stored `order`
  const groups = (categories || []).map((c) => ({
    category: c,
    items: products
      .filter((p) => p.category === c.slug)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
  }));
  // Bucket for products in categories that no longer exist
  const knownSlugs = new Set((categories || []).map((c) => c.slug));
  const orphan = products
    .filter((p) => !knownSlugs.has(p.category))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  if (orphan.length) {
    groups.push({
      category: { slug: "__orphan__", title: "Uncategorized / missing category" },
      items: orphan,
    });
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const [collapsed, setCollapsed] = useState({});

  const handleDragEnd = (categorySlug, items) => async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = items.map((p) => p.product_id);
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(ids, oldIndex, newIndex);
    onReorder(categorySlug, next);
  };

  return (
    <div className="space-y-4">
      {groups.map((g) => {
        if (!g.items.length) return null;
        const isCollapsed = !!collapsed[g.category.slug];
        return (
          <div key={g.category.slug} className="border border-border bg-white" data-testid={`product-group-${g.category.slug}`}>
            <button
              type="button"
              onClick={() => setCollapsed((c) => ({ ...c, [g.category.slug]: !c[g.category.slug] }))}
              className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-secondary/40 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="overline truncate">{g.category.title}</span>
                <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-mono border border-border bg-secondary text-foreground rounded shrink-0">
                  {g.items.length}
                </span>
              </div>
              {isCollapsed ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronUp className="w-4 h-4 text-muted-foreground" />}
            </button>
            {!isCollapsed && (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd(g.category.slug, g.items)}
              >
                <SortableContext
                  items={g.items.map((p) => p.product_id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="divide-y divide-border border-t border-border">
                    {g.items.map((p) => (
                      <SortableProductRow
                        key={p.product_id}
                        p={p}
                        onEdit={onEdit}
                        onDuplicate={onDuplicate}
                        onDelete={onDelete}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        );
      })}
    </div>
  );
}
